/*#
# Sun and shadows (b3d-shadows)

`B3dSun` — directional light + cascaded shadow generator. Add a `<tosi-b3d-sun>`
inside `<tosi-b3d>` and every registered mesh starts casting shadows onto every
registered surface. When `b3dSkybox` is present, the sun's direction, color, and
intensity are driven by the skybox's time-of-day; otherwise the static
`x`/`y`/`z`/`intensity` attributes apply.

Cascaded shadow maps (CSM) cover the camera's view frustum out to `shadowMaxZ`,
giving high-resolution shadows near the camera and graceful falloff at distance
— the right model for both close scenes and fast, far-ranging ones (see the
aircraft demo). `activeDistance` gates which meshes participate; out-of-range
meshes are skipped to keep the shadow map tight.

> **Tip.** A huge ground that *casts* shadows expands the shadow frustum to fit
> it, shrinking everything else to sub-pixel. Suffix big ground meshes with
> `_nocast` so they only receive.

## Demo

```js
import { b3d, b3dSun, b3dLight, b3dSkybox, b3dGround, b3dSphere, label3d, slider3d } from 'tosijs-3d'
import { orbitCam } from 'tosijs-3d/demo-utils'
import { tosi } from 'tosijs'

const { sun } = tosi({ sun: { timeOfDay: 10 } })

preview.append(
  b3d(
    {
      panelTitle: 'Sun',
      scenePanel: () => [
        slider3d({ label: 'time of day', value: sun.timeOfDay, min: 5, max: 19, step: 0.25 }),
      ],
      sceneCreated(el, BABYLON) {
        const camera = orbitCam(el, {
          alpha: -Math.PI / 2.5, beta: Math.PI / 3, radius: 8,
          target: [0, 1, 0], maxElevationDeg: 70,
        })
        camera.lowerRadiusLimit = 3
        camera.upperRadiusLimit = 24
      },
    },
    b3dLight({ intensity: 0.3 }),
    b3dSkybox({ timeOfDay: sun.timeOfDay, realtimeScale: 0 }),
    b3dSun({ shadowTextureSize: 2048 }),
    b3dGround({ meshName: 'ground_nocast', width: 20, height: 20, color: '#7d9b6e' }),
    b3dSphere({ y: 1.2, diameter: 1.5, color: '#cc4422' }),
    b3dSphere({ y: 0.8, diameter: 1, x: 2, z: -1, color: '#4488cc' }),
  ),
)
```
```css
tosi-b3d { width: 100%; height: 100%; }
```

## Attributes

| Attribute | Default | Description |
|-----------|---------|-------------|
| `x` | `0` | Sun direction X (overridden by skybox when present) |
| `y` | `-1` | Sun direction Y |
| `z` | `-0.5` | Sun direction Z |
| `intensity` | `1` | Sun intensity (overridden by skybox when present) |
| `shadows` | `'on'` | `'off'`: the sun casts no shadows (the light stays). Shadows are always cascaded; there is no `shadowCascading` attribute, and passing one does nothing |
| `shadowTextureSize` | `auto` | Shadow map resolution (per cascade); `auto` = device tier |
| `shadowMaxZ` | `100` | Far plane of the cascaded shadow frustum |
| `shadowDarkness` | `0.1` | 0 = fully dark shadow, 1 = no shadow |
| `shadowNormalBias` | `0.04` | Offset along the surface normal — **the shadow-acne knob**. Lower it if shadows detach from small casters; much past 0.05 and thin limbs lose their shadow. A scene about a character wants about `0.008`: the default pulls a body's silhouette in by centimetres under a low sun, and its shadow comes out thin |
| `shadowBias` | `0.002` | Depth-direction bias, in the cascade's depth range. Together with `shadowNormalBias` it clears the fine bands acne leaves on lit surfaces |
| `numCascades` | `auto` | Number of cascade splits (1–4); `auto` = device tier |
| `stabilizeCascades` | `'on'` | Reduce shadow-edge "swimming" under motion |
| `lambda` | `0.8` | Cascade split blend (0 = uniform, 1 = logarithmic) |
| `cascadeBlendPercentage` | `0.1` | Soft blend across cascade seams |
| `activeDistance` | `30` | Max camera-to-mesh distance a caster participates from |
| `updateIntervalMs` | `1000` | How often to re-evaluate active casters |

## Mesh suffix conventions

| Suffix | Effect |
|--------|--------|
| `_nocast` (or `-nocast`) | Mesh doesn't cast shadows (e.g. huge ground planes) |
| `_noshadow` (or `-noshadow`) | Mesh doesn't receive shadows |
*/
/*{ "parent": "sky-and-light", "order": 20 }*/

import * as BABYLON from '@babylonjs/core'
import { conventionName, actualMeshes, B3dChild, isOff } from './b3d-utils.js'
import { headsetDevice, resolveBudget } from './b3d-quality.js'
import type { B3d, SceneAdditions, SceneAdditionHandler } from './tosi-b3d.js'

/*
THE SHADOWS FOLLOW THE CAMERA, whatever order things happen in (#3420).

Babylon's cascaded generator works out where its cascades split from the
camera's near and far planes, and does it again only when one of its OWN
settings changes. A camera change never tells it: switch cameras, enter a
headset, or create the sun while the camera is unusual, and the splits are
the old camera's for good. Its `shadowMaxZ` setter also ignores a value
the camera of the moment does not admit. The rig tool hit both: a sun made
while an orthographic face view held the near plane behind the camera
(`inspectView` does, so zooming in cannot cut the model open) computed
NaN splits from it, and the whole scene rendered black from then on, back in
perspective too. A sun declared before the view was fine, which is what
made it look like a race.

So, just before the shadow map renders: if the camera, its near or far plane
or its projection changed, split again and re-apply `shadowMaxZ`. And while
the near plane is at or behind the camera, the splits are worked out from a
small positive one (the cascades start just in front of the camera), and the
camera's own is put back straight after.
*/
export function cascadesFollowCamera(
  gen: BABYLON.CascadedShadowGenerator,
  shadowMaxZ: () => number
): void {
  const map = gen.getShadowMap()
  if (map == null) return
  const scene = map.getScene()
  if (scene == null) return
  let seen = ''
  let lent: { camera: BABYLON.Camera; minZ: number } | null = null
  // First, before the generator's own observer splits and builds matrices.
  map.onBeforeBindObservable.add(
    () => {
      const camera = scene.activeCamera
      if (camera == null) return
      if (camera.minZ <= 0) {
        lent = { camera, minZ: camera.minZ }
        camera.minZ = Math.max(0.01, camera.maxZ * 1e-5)
      }
      const now = `${camera.uniqueId}|${camera.minZ}|${camera.maxZ}|${camera.mode}`
      if (now !== seen) {
        seen = now
        gen.shadowMaxZ = shadowMaxZ()
        gen.splitFrustum()
      }
    },
    undefined,
    true
  )
  // Last: the camera gets its own near plane back before anything draws.
  map.onBeforeBindObservable.add(() => {
    if (lent != null) {
      lent.camera.minZ = lent.minZ
      lent = null
    }
  })
}

export class B3dSun extends B3dChild {
  static preferredTagName = 'tosi-b3d-sun'

  static initAttributes = {
    shadowMaxZ: 100,
    shadowDarkness: 0.1,
    /**
     * Offset the shadow lookup ALONG THE SURFACE NORMAL, in world units: the
     * main acne knob. At Babylon's default of 0 a large ground self-shadows
     * into a stipple over the whole surface.
     *
     * Lower it if shadows detach from small objects ("peter-panning"); raise
     * it much past 0.05 and thin limbs lose their shadow. 0.04 with the depth
     * bias below is clean on a plain checker ground and a lit box (the glass
     * gamepad demo, 1024 map), with a character's limbs and feet still shadowed.
     */
    shadowNormalBias: 0.04,
    /**
     * Depth-direction bias, in the cascade's depth range. Babylon's CSM default
     * (0.00005) leaves fine bands over every lit surface of a plain scene; a
     * mottled ground hides them, which is why a textured demo can look clean
     * while a checker one stripes.
     */
    shadowBias: 0.002,
    // 0 = auto: resolved from the device quality tier (see b3d-quality). Set an
    // explicit value to override. shadowTextureSize is fixed at generator creation.
    shadowTextureSize: 0,
    activeDistance: 30,
    // Cascaded shadow map (CSM) tuning. CSM covers the camera's view frustum
    // out to shadowMaxZ, which is why it works for both close scenes and fast,
    // far-ranging ones (e.g. aircraft) — see the aircraft demo. 0 = auto (tier).
    numCascades: 0,
    // stabilize reduces shadow-edge "swimming" as the camera moves quickly.
    stabilizeCascades: 'on' as 'on' | 'off',
    // 'off': no shadows from this sun (the generator stays, idle, so turning
    // them back on is immediate).
    shadows: 'on' as 'on' | 'off',
    // lambda: 0 = uniform cascade splits, 1 = logarithmic (more resolution
    // near the camera). 0.8 is a good default; raise toward 1 for big depth.
    lambda: 0.8,
    // soft blend across cascade boundaries to hide the seams
    cascadeBlendPercentage: 0.1,
    x: 0,
    y: -1,
    z: -0.5,
    intensity: 1,
    updateIntervalMs: 1000,
  }

  owner: B3d | null = null
  light?: BABYLON.DirectionalLight
  shadowGenerator?: BABYLON.CascadedShadowGenerator
  shadowCasters: BABYLON.Mesh[] = []
  activeShadowCasters: BABYLON.Mesh[] = []

  private interval = 0
  private _callback?: SceneAdditionHandler
  private _update?: () => void

  private shadowCallback(additions: SceneAdditions): void {
    const { meshes } = additions
    if (meshes == null) return
    for (const mesh of actualMeshes(meshes)) {
      if (
        !conventionName(mesh.name).includes('_nocast') &&
        !mesh.name.includes('-nocast') &&
        !this.shadowCasters.includes(mesh)
      ) {
        this.shadowCasters.push(mesh)
      }
      /*
      AN INSTANCE INHERITS THIS FROM ITS SOURCE — do not write it.

      Babylon warns "Setting receiveShadows on an instanced mesh has no effect"
      once PER INSTANCE, and a library-backed scene has thousands: ensemble
      measured ~10,000 messages on a single load, mostly in one burst, which
      drowns the console outright (tosijs-3d#53).

      Skipping it cannot regress anything, and that is the point: the write was
      ALREADY a no-op. An `InstancedMesh` takes the flag from the mesh it
      instances, so Babylon ignored the assignment and only warned. Nothing that
      received shadows before stops now — a source mesh that is registered still
      gets the flag here, and one that is not never got it anyway.

      (`actualMeshes` does not filter these out: it tests `geometry != null`, and
      an instance proxies its source's geometry, so instances arrive here looking
      exactly like meshes. That is why the guard has to be explicit.)
      */
      if (mesh instanceof BABYLON.InstancedMesh) continue
      mesh.receiveShadows =
        !conventionName(mesh.name).includes('_noshadow') &&
        !conventionName(mesh.name).includes('-noshadow')
    }
  }

  private baseIntensity = 1

  /**
   * Underwater dimming multiplier (1 above water). Owned by the sun, applied by
   * whoever writes the final intensity. A `b3dSkybox` reads this and multiplies
   * its day/night intensity by it, so the two never fight over `light.intensity`.
   */
  dimFactor = 1

  /**
   * Set true by a `b3dSkybox` that owns the day/night intensity cycle. While
   * set, the sun stops writing `light.intensity` itself (it only maintains
   * `dimFactor`), so the skybox's 100ms cycle isn't stomped by this 1s update.
   */
  externallyLit = false

  private update() {
    if (this.light == null || this.owner?.scene == null) return
    // Get the actual world-space position of the active camera
    const activeCamera = this.owner.scene.activeCamera
    if (activeCamera == null) return
    const target = activeCamera.globalPosition

    this.light.position.x = target.x
    this.light.position.y = target.y + 10
    this.light.position.z = target.z

    // Dim sun when camera is underwater. We only compute the factor here; the
    // final write happens below (or in b3dSkybox when it owns the cycle), so a
    // day/night skybox and this 1s update never stomp each other's intensity.
    const waterMesh = this.owner.scene.getMeshByName('water_nocast')
    let dimFactor = 1
    if (waterMesh && target.y < waterMesh.absolutePosition.y) {
      const depth = waterMesh.absolutePosition.y - target.y
      dimFactor = Math.max(0.05, 1 - depth * 0.5)
    }
    this.dimFactor = dimFactor
    // When a skybox owns the day/night cycle it writes the final intensity
    // (base * dimFactor) on its own faster cadence; don't double-write here.
    if (!this.externallyLit) {
      this.light.intensity = this.baseIntensity * dimFactor
    }

    /*
    DROP THE DEAD FIRST. A caster that has been disposed is still in these
    lists — nothing tells the sun — and it stays in the generator's render
    list, where it is drawn into the shadow map every frame forever. A scene
    that spawns and despawns (a crowd slider, a spawner, anything pooled) leaks
    a shadow caster per corpse, and the symptom is a shadow map that gets
    slower with time rather than an error.
    */
    for (let i = this.shadowCasters.length - 1; i >= 0; i--) {
      const mesh = this.shadowCasters[i]
      // Gone, or renamed not to cast since it registered (a city names its
      // roads `_nocast` after the library has registered them).
      const nocast =
        conventionName(mesh.name).includes('_nocast') ||
        mesh.name.includes('-nocast')
      if (!mesh.isDisposed() && !nocast) continue
      this.shadowCasters.splice(i, 1)
      const active = this.activeShadowCasters.indexOf(mesh)
      if (active > -1) {
        this.activeShadowCasters.splice(active, 1)
        this.shadowGenerator!.removeShadowCaster(mesh)
      }
    }

    const activeDistance = (this as any).activeDistance as number
    for (const mesh of this.shadowCasters) {
      const distance = mesh.getAbsolutePosition().subtract(target).length()
      if (distance < activeDistance) {
        if (!this.activeShadowCasters.includes(mesh)) {
          this.activeShadowCasters.push(mesh)
          this.shadowGenerator!.addShadowCaster(mesh)
        }
      } else {
        const idx = this.activeShadowCasters.indexOf(mesh)
        if (idx > -1) {
          this.activeShadowCasters.splice(idx, 1)
          this.shadowGenerator!.removeShadowCaster(mesh)
        }
      }
    }
  }

  sceneReady(owner: B3d, scene: BABYLON.Scene) {
    this.owner = owner
    const attrs = this as any
    this._update = this.update.bind(this)
    this.interval = window.setInterval(this._update!, attrs.updateIntervalMs)

    const light = new BABYLON.DirectionalLight(
      'sun',
      new BABYLON.Vector3(attrs.x, attrs.y, attrs.z),
      scene
    )
    light.intensity = attrs.intensity
    this.baseIntensity = attrs.intensity
    this.light = light

    this.shadowGenerator = new BABYLON.CascadedShadowGenerator(
      // Sized once, so on a standalone headset sized for the XR tier now.
      resolveBudget(attrs.shadowTextureSize, 'shadowTextureSize', {
        xr: headsetDevice(),
      }),
      light
    )

    cascadesFollowCamera(this.shadowGenerator, () => (this as any).shadowMaxZ)

    this._callback = this.shadowCallback.bind(this)
    owner.addSceneListener(this._callback)

    // Apply shadow settings now. render() also calls this, but render() may
    // never fire again after the generator exists (e.g. a static sun with no
    // day/night cycle), so the generator would otherwise keep Babylon's
    // defaults — wrong filter, no shadowMaxZ — and cast no visible shadow.
    this.configureShadows()
  }

  private configureShadows() {
    const attrs = this as any
    if (this.light == null || this.shadowGenerator == null) return

    // Direction is intentionally NOT re-applied here. The DirectionalLight
    // constructor sets initial direction from attrs.x/y/z; thereafter b3dSkybox
    // (when present) owns light.direction via its day/night cycle. Re-writing
    // direction on every render() would stomp the skybox's writes whenever any
    // other sun attr changed.
    this.baseIntensity = attrs.intensity

    // Soften shadows when light is dim (moonlight)
    const darkness =
      attrs.intensity < 0.5
        ? attrs.shadowDarkness + (1 - attrs.shadowDarkness) * 0.6
        : attrs.shadowDarkness
    this.shadowGenerator.setDarkness(darkness)

    /*
    ACNE. Babylon's CSM defaults are bias 0.00005 / normalBias 0, and a normal
    bias of ZERO is not a tuned value for a large receiver — a 40x40 ground
    self-shadowed into a fine stipple across its entire surface (see the
    `shadowNormalBias` note). This was previously left alone with a comment
    saying overriding it "tends to cause peter-panning"; that is true of DEPTH
    bias, which is why the default moved on `normalBias` instead, and was
    checked against a live scene at 0, 0.02 and 0.05 rather than reasoned about.
    */
    this.shadowGenerator.bias = attrs.shadowBias
    this.shadowGenerator.normalBias = attrs.shadowNormalBias
    this.shadowGenerator.numCascades = resolveBudget(
      attrs.numCascades,
      'numCascades',
      { xr: headsetDevice() }
    )
    this.shadowGenerator.shadowMaxZ = attrs.shadowMaxZ
    this.shadowGenerator.stabilizeCascades = !isOff(attrs.stabilizeCascades)
    this.light.shadowEnabled = !isOff(attrs.shadows)
    this.shadowGenerator.lambda = attrs.lambda
    this.shadowGenerator.cascadeBlendPercentage = attrs.cascadeBlendPercentage
  }

  sceneDispose() {
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = 0
    }
    if (this.owner && this._callback) {
      this.owner.removeSceneListener(this._callback)
    }
    if (this.light != null) {
      this.light.dispose()
      this.light = undefined
      this.shadowGenerator = undefined
    }
    this.shadowCasters = []
    this.activeShadowCasters = []
    this.owner = null
  }

  render() {
    super.render()
    this.configureShadows()
  }
}

export const b3dSun = B3dSun.elementCreator()
